<template>
  <div class="account-service-page">
    <!-- 1. Hero Section -->
    <section class="hero-section-account">
      <div class="container">
        <div class="hero-content-account">
          <div class="hero-text-account">
            <h1>{{ $t("accountServicePage.hero.title") }}</h1>
            <p class="subtitle">{{ $t("accountServicePage.hero.subtitle") }}</p>
            <div class="hero-cta-buttons">
              <div class="hero-qr-item">
                <div class="hero-qr-label">WhatsApp</div>
                <img
                  :src="whatsAppQr"
                  alt="WhatsApp QR"
                  class="hero-qr-image"
                  @click="openQrPreview(whatsAppQr)"
                />
              </div>
              <div class="hero-qr-item">
                <div class="hero-qr-label">Telegram</div>
                <img
                  :src="telegramQr"
                  alt="Telegram QR"
                  class="hero-qr-image"
                  @click="openQrPreview(telegramQr)"
                />
              </div>
            </div>
          </div>
          <div class="hero-image-account">
            <img
              :src="heroImageAccount"
              :alt="$t('accountServicePage.hero.imageAlt')"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 2. Accounts for All Platforms -->
    <section class="all-platforms-section section-padding">
      <div class="container">
        <h2 class="section-title text-center">
          {{ $t("accountServicePage.allPlatforms.title") }}
        </h2>
        <p class="section-description text-center">
          {{ $t("accountServicePage.allPlatforms.description") }}
        </p>
        <div class="platforms-grid-detailed">
          <div
            v-for="platform in allPlatforms"
            :key="platform.key"
            class="platform-item-detailed"
          >
            <div class="platform-logo-wrapper">
              <img
                :src="platform.logo"
                :alt="platform.name"
                class="platform-logo-detailed"
              />
            </div>
            <div class="platform-text-content">
              <h3>{{ platform.name }}</h3>
              <p>{{ $t(platform.descriptionKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. Stats Section -->
    <section
      class="stats-section-account section-padding bg-light-blue-gradient"
    >
      <div class="container">
        <div class="stats-grid-account">
          <div
            v-for="stat in stats"
            :key="stat.labelKey"
            class="stat-item-account"
          >
            <div class="stat-value">{{ $t(stat.valueKey) }}</div>
            <div class="stat-label">{{ $t(stat.labelKey) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. Old Accounts Solution -->
    <section class="old-accounts-solution-section section-padding">
      <div class="container old-accounts-content">
        <div class="old-accounts-text">
          <h2>{{ $t("accountServicePage.oldAccounts.title") }}</h2>
          <p>{{ $t("accountServicePage.oldAccounts.p1") }}</p>
          <ul>
            <li v-for="item in oldAccountFeatures" :key="item.key">
              <strong>{{ $t(item.titleKey) }}</strong>
              {{ $t(item.descriptionKey) }}
            </li>
          </ul>
          <p>{{ $t("accountServicePage.oldAccounts.p2") }}</p>
          <div class="old-accounts-buttons">
            <button class="cta-button-outline">
              {{ $t("accountServicePage.oldAccounts.button1") }}
            </button>
            <button class="cta-button-solid">
              {{ $t("accountServicePage.oldAccounts.button2") }}
            </button>
          </div>
        </div>
        <div class="old-accounts-image">
          <img
            :src="oldAccountsIllustration"
            :alt="$t('accountServicePage.oldAccounts.imageAlt')"
          />
        </div>
      </div>
    </section>

    <!-- 5. 24*7 Online Service -->
    <section
      class="online-service-section section-padding bg-light-blue-gradient"
    >
      <div class="container online-service-content">
        <div class="online-service-image">
          <img
            :src="onlineServiceIllustration"
            :alt="$t('accountServicePage.onlineService.imageAlt')"
          />
        </div>
        <div class="online-service-text">
          <h2>{{ $t("accountServicePage.onlineService.title") }}</h2>
          <p>{{ $t("accountServicePage.onlineService.description") }}</p>
        </div>
      </div>
    </section>

    <!-- 6. Support for Any Verticals -->
    <section class="verticals-support-section section-padding">
      <div class="container verticals-support-content">
        <div class="verticals-support-text">
          <h2>{{ $t("accountServicePage.verticalsSupport.title") }}</h2>
          <p>{{ $t("accountServicePage.verticalsSupport.p1") }}</p>
          <ul>
            <li v-for="item in verticalsFeatures" :key="item.key">
              {{ $t(item.textKey) }}
            </li>
          </ul>
          <p>{{ $t("accountServicePage.verticalsSupport.p2") }}</p>
        </div>
        <div class="verticals-support-image">
          <img
            :src="verticalsSupportIllustration"
            :alt="$t('accountServicePage.verticalsSupport.imageAlt')"
          />
        </div>
      </div>
    </section>

    <!-- QR 放大预览 -->
    <div
      v-if="qrPreviewVisible"
      class="qr-preview-overlay"
      @click.self="closeQrPreview"
    >
      <div class="qr-preview-content">
        <img :src="qrPreviewSrc" alt="QR Preview" class="qr-preview-image" />
        <button class="qr-preview-close" @click="closeQrPreview">×</button>
      </div>
    </div>
  </div>
</template>

<script>
// --- 导入图片资源 ---
// Hero

import heroImageAccountPath from "@/assets/account-service/kh.png";
import whatsAppQrPath from "@/assets/account-service/WhatsApp.webp";
import telegramQrPath from "@/assets/account-service/telegram.webp";
// Platform Logos (需要为所有9个平台准备logo)
import bingLogoPath from "@/assets/account-service/Bing.png";
import googleColorLogoPath from "@/assets/account-service/google.png"; // 与页脚区分
import metaLogoPath from "@/assets/account-service/Meta.png";
import taboolaLogoPath from "@/assets/account-service/Taboola.png";
import mediagoLogoPath from "@/assets/account-service/Mediago.png";
import outbrainLogoPath from "@/assets/account-service/Outbrain.png";
import tiktokColorLogoPath from "@/assets/account-service/tt.png"; // 与页脚区分
import kwaiColorLogoPath from "@/assets/account-service/Kwai.png"; // 与页脚区分
import bigoadsLogoPath from "@/assets/account-service/Bigo Ads.png";
// Illustrations
import oldAccountsIllustrationPath from "@/assets/account-service/p1.png";
import onlineServiceIllustrationPath from "@/assets/account-service/p2.png";
import verticalsSupportIllustrationPath from "@/assets/account-service/p3.png";

export default {
  name: "AccountServicePage",
  data() {
    return {
      qrPreviewVisible: false,
      qrPreviewSrc: "",
      whatsAppQr: whatsAppQrPath,
      telegramQr: telegramQrPath,
      heroImageAccount: heroImageAccountPath,
      allPlatforms: [
        {
          key: "bing",
          name: "Bing",
          logo: bingLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.bing",
        },
        {
          key: "google",
          name: "Google",
          logo: googleColorLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.google",
        },
        {
          key: "meta",
          name: "Meta",
          logo: metaLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.meta",
        },
        {
          key: "taboola",
          name: "Taboola",
          logo: taboolaLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.taboola",
        },
        {
          key: "mediago",
          name: "Mediago",
          logo: mediagoLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.mediago",
        },
        {
          key: "outbrain",
          name: "Outbrain",
          logo: outbrainLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.outbrain",
        },
        {
          key: "tiktok",
          name: "Tiktok",
          logo: tiktokColorLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.tiktok",
        },
        {
          key: "kwai",
          name: "Kwai",
          logo: kwaiColorLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.kwai",
        },
        {
          key: "bigoads",
          name: "Bigo Ads",
          logo: bigoadsLogoPath,
          descriptionKey: "accountServicePage.allPlatforms.platforms.bigoads",
        },
      ],
      stats: [
        {
          valueKey: "accountServicePage.stats.years.value",
          labelKey: "accountServicePage.stats.years.label",
        },
        {
          valueKey: "accountServicePage.stats.accountsDelivered.value",
          labelKey: "accountServicePage.stats.accountsDelivered.label",
        },
        {
          valueKey: "accountServicePage.stats.agencyAccounts.value",
          labelKey: "accountServicePage.stats.agencyAccounts.label",
        },
        {
          valueKey: "accountServicePage.stats.totalClients.value",
          labelKey: "accountServicePage.stats.totalClients.label",
        },
        {
          valueKey: "accountServicePage.stats.platformsSupported.value",
          labelKey: "accountServicePage.stats.platformsSupported.label",
        },
        {
          valueKey: "accountServicePage.stats.tradingVolume.value",
          labelKey: "accountServicePage.stats.tradingVolume.label",
        },
      ],
      oldAccountFeatures: [
        {
          key: "f1",
          titleKey: "accountServicePage.oldAccounts.features.expedite.title",
          descriptionKey:
            "accountServicePage.oldAccounts.features.expedite.desc",
        },
        {
          key: "f2",
          titleKey: "accountServicePage.oldAccounts.features.stability.title",
          descriptionKey:
            "accountServicePage.oldAccounts.features.stability.desc",
        },
        {
          key: "f3",
          titleKey:
            "accountServicePage.oldAccounts.features.effectiveness.title",
          descriptionKey:
            "accountServicePage.oldAccounts.features.effectiveness.desc",
        },
      ],
      oldAccountsIllustration: oldAccountsIllustrationPath,
      onlineServiceIllustration: onlineServiceIllustrationPath,
      verticalsFeatures: [
        {
          key: "v1",
          textKey: "accountServicePage.verticalsSupport.features.proven",
        },
        {
          key: "v2",
          textKey: "accountServicePage.verticalsSupport.features.optimize",
        },
        {
          key: "v3",
          textKey: "accountServicePage.verticalsSupport.features.navigate",
        },
      ],
      verticalsSupportIllustration: verticalsSupportIllustrationPath,
    };
  },
  methods: {
    openQrPreview(src) {
      this.qrPreviewSrc = src;
      this.qrPreviewVisible = true;
    },
    closeQrPreview() {
      this.qrPreviewVisible = false;
      this.qrPreviewSrc = "";
    },
  },
};
</script>

<style scoped>
.account-service-page {
  font-family: "Inter", sans-serif; /* Or your preferred font */
  color: #333;
  background-color: #f4f8ff; /* Light blue/grayish background from design */
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-padding {
  padding: 60px 0;
}
@media (min-width: 768px) {
  .section-padding {
    padding: 80px 0;
  }
}

.text-center {
  text-align: center;
}

.section-title {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  font-size: 2.5rem; /* ~40px */
  font-weight: 700;
  color: #1a2b4d; /* Dark blue for titles */
  margin-bottom: 20px;
}
.section-description {
  font-size: 1.1rem;
  color: #5a677d;
  margin-bottom: 40px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

/* 1. Hero Section */
.hero-section-account {
  background-color: #e0edff; /* Lighter blue for hero */
  padding-top: 60px;
  padding-bottom: 60px;
}
.hero-content-account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}
.hero-text-account {
  flex: 1;
  max-width: 600px;
}
.hero-text-account h1 {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  font-size: 3rem; /* ~56px */
  font-weight: 800;
  color: #0d244f;
  line-height: 1.2;
  margin-bottom: 16px;
}
.hero-text-account .subtitle {
  font-size: 1.1rem;
  color: #4a5c7a;
  margin-bottom: 30px;
  line-height: 1.6;
}
.hero-cta-buttons {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  margin-left: 40px;
}
.hero-qr-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.hero-qr-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #4a5c7a;
}
.hero-qr-image {
  width: 140px;
  height: 140px;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}
.cta-button {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 25px; /* Pill shape */
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  transition: transform 0.2s ease;
}
.cta-button:hover {
  transform: translateY(-2px);
}
.cta-button img {
  height: 20px;
  margin-right: 8px;
}
.cta-button.whatsapp {
  background-color: #25d366;
  color: white;
}
.cta-button.telegram {
  background-color: #0088cc;
  color: white;
}

.hero-image-account {
  flex: 0 0 45%; /* Image takes about 45% of width */
  max-width: 500px;
}
.hero-image-account img {
  width: 100%;
  height: auto;
  object-fit: contain;
}

/* 2. All Platforms Section */
.platforms-grid-detailed {
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(320px, 1fr)
  ); /* 稍微调整minmax，因为内容现在是水平的 */
  gap: 30px;
}
.platform-item-detailed {
  background-color: #fff;
  border-radius: 12px;
  padding: 20px; /* 调整内边距 */
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.3s ease;
  display: flex; /* 将项目设为 flex 容器 */
  align-items: center; /* 垂直居中 Logo 和文本内容 */
  gap: 20px; /* Logo 和文本内容之间的间距 */
}
.platform-item-detailed:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
}

.platform-logo-wrapper {
  /* 新增样式，用于包裹logo */
  flex-shrink: 0; /* 防止logo被压缩 */
  width: 50px; /* 设置logo包裹层的宽度 */
  height: 50px; /* 设置logo包裹层的高度 */
  display: flex;
  align-items: center;
  justify-content: center;
  /* background-color: #f0f4f8; /* 可选：给logo一个背景板 */
  /* border-radius: 8px; */
}
.platform-logo-detailed {
  max-width: 100%; /* Logo 图片适应其包裹层 */
  max-height: 100%;
  object-fit: contain;
  /* margin-bottom: 0; /* 之前是15px，现在由flex的gap控制 */
}

.platform-text-content {
  /* 新增样式，用于包裹 H3 和 P */
  flex-grow: 1; /* 让文本内容占据剩余空间 */
  text-align: left; /* 确保文本是左对齐的 */
}

.platform-item-detailed h3 {
  font-size: 1.2rem; /* 调整字号 */
  font-weight: 600;
  color: #1a2b4d;
  margin-top: 0; /* 移除H3默认的上边距 */
  margin-bottom: 6px; /* H3 和 P 之间的间距 */
}
.platform-item-detailed p {
  font-size: 0.85rem; /* 调整字号 */
  color: #5a677d;
  line-height: 1.5;
  margin: 0; /* 移除P默认的边距 */
}

/* 3. Stats Section */
.bg-light-blue-gradient {
  /* Re-using if it gives a similar feel */
  background: linear-gradient(135deg, #e0f2fe 0%, #cce3ff 100%);
}
.stats-grid-account {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  background-color: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.07);
}
.stat-item-account {
  text-align: center;
  padding: 15px;
}
.stat-value {
  font-size: 2.2rem;
  font-weight: 700;
  color: #0052cc; /* Blue for stats */
  margin-bottom: 5px;
}
.stat-label {
  font-size: 0.9rem;
  color: #4a5c7a;
  text-transform: uppercase;
}

/* 4. Old Accounts Solution Section */
.old-accounts-content {
  display: flex;
  align-items: center;
  gap: 50px;
}
.old-accounts-text {
  flex: 1;
}
.old-accounts-text h2 {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  font-size: 2.2rem;
  color: #1a2b4d;
  margin-bottom: 20px;
}
.old-accounts-text p {
  font-size: 1rem;
  line-height: 1.7;
  color: #4a5c7a;
  margin-bottom: 15px;
}
.old-accounts-text ul {
  list-style: none;
  padding-left: 0;
  margin-bottom: 20px;
}
.old-accounts-text li {
  font-size: 1rem;
  color: #4a5c7a;
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
}
.old-accounts-text li::before {
  content: "✔"; /* Checkmark or other icon */
  color: #007bff;
  position: absolute;
  left: 0;
  font-weight: bold;
}
.old-accounts-text li strong {
  color: #2d3e5f;
}
.old-accounts-buttons {
  display: flex;
  gap: 15px;
  margin-top: 30px;
}
.cta-button-outline,
.cta-button-solid {
  padding: 10px 25px;
  border-radius: 25px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  font-size: 0.95rem;
}
.cta-button-outline {
  border: 2px solid #007bff;
  color: #007bff;
  background-color: transparent;
}
.cta-button-outline:hover {
  background-color: #007bff;
  color: white;
}
.cta-button-solid {
  background-color: #007bff;
  color: white;
  border: 2px solid #007bff;
}
.cta-button-solid:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}
.old-accounts-image {
  flex: 0 0 40%;
  max-width: 450px;
}
.old-accounts-image img {
  width: 100%;
  height: auto;
}

/* 5. Online Service Section */
.online-service-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  background-color: #fff;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.07);
}
.online-service-text {
  flex: 1;
}
.online-service-text h2 {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  font-size: 2.2rem;
  color: #007bff; /* Blue title */
  margin-bottom: 15px;
}
.online-service-text p {
  font-size: 1.1rem;
  color: #4a5c7a;
  line-height: 1.6;
}
.online-service-image {
  flex: 0 0 40%;
  max-width: 400px;
}
.online-service-image img {
  width: 100%;
  height: auto;
}

/* 6. Verticals Support Section */
.verticals-support-content {
  display: flex;
  align-items: center;
  gap: 50px;
}
.verticals-support-text {
  flex: 1;
}
.verticals-support-text h2 {
  font-family: "Acumin Variable Concept", "Inter", sans-serif;
  font-size: 2.2rem;
  color: #1a2b4d;
  margin-bottom: 20px;
}
.verticals-support-text p {
  font-size: 1rem;
  line-height: 1.7;
  color: #4a5c7a;
  margin-bottom: 15px;
}
.verticals-support-text ul {
  list-style: none;
  padding-left: 0;
  margin-bottom: 20px;
}
.verticals-support-text li {
  font-size: 1rem;
  color: #4a5c7a;
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
}
.verticals-support-text li::before {
  content: "•";
  color: #007bff;
  position: absolute;
  left: 0;
  font-weight: bold;
}
.verticals-support-image {
  flex: 0 0 45%;
  max-width: 500px;
}
.verticals-support-image img {
  width: 100%;
  height: auto;
}

.qr-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.qr-preview-content {
  position: relative;
  background: #ffffff;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
}

.qr-preview-image {
  width: 320px;
  height: 320px;
  object-fit: cover;
  border-radius: 12px;
}

.qr-preview-close {
  position: absolute;
  top: 2px;
  right: 6px;
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .hero-text-account h1 {
    font-size: 2.8rem;
  }
  .old-accounts-content,
  .verticals-support-content {
    flex-direction: column;
    text-align: center;
  }
  .old-accounts-image,
  .verticals-support-image,
  .online-service-image {
    margin-top: 30px;
    max-width: 80%;
  }
  .online-service-content {
    flex-direction: column-reverse;
    text-align: center;
  } /* Image below text */
  .old-accounts-buttons {
    justify-content: center;
  }
}
@media (max-width: 768px) {
  .hero-content-account {
    flex-direction: column;
    text-align: center;
  }
  .hero-image-account {
    margin-top: 30px;
  }
  .hero-text-account h1 {
    font-size: 2.5rem;
  }
  .section-title {
    font-size: 2rem;
  }
  .old-accounts-text h2,
  .online-service-text h2,
  .verticals-support-text h2 {
    font-size: 1.8rem;
  }
  .platforms-grid-detailed {
    grid-template-columns: 1fr;
  } /* Stack platforms on smaller screens */
}
</style>
